<template>
  <section id="customer-detail">
    <div
      v-if="loading"
      class="loading-container"
    >
      <img src="@shared/assets/img/general/loading.svg" alt="Loading">
    </div>
    <div
      v-else
      class="content-container"
    >
      <TopInfo />
      <DetailTab />
      <div class="body-container">
        <div
          v-if="activeTab === 'overview'"
          class="cd-content-container"
        >
          <Overview />
        </div>
        <div
          v-else-if="activeTab === 'cycle'"
          class="cd-content-container"
        >
          <CycleFilter />
          <CycleTable v-if="cycleViewTab === 'table'" />
          <CycleGraph
            v-else-if="cycleViewTab === 'chart'"
            :graphData="cycleTableData"
            :dailyStats="dailyStats"
          />
        </div>
        <div
          v-else-if="activeTab === 'deltaT'"
          class="cd-content-container"
        >
          <div class="grid-column ai-c jc-b">
            <CycleFilter />
            <div class="grid-column ai-c gap-10">
              <button
                type="button"
                class="toggle-button"
                :class="[!lightMode ? 'dark-active' : '']"
                @click="lightMode = false"
              >
                <span class="icon-luc-Moon sac__font-24" />
              </button>
              <button
                type="button"
                class="toggle-button"
                :class="[lightMode ? 'light-active' : '']"
                @click="lightMode = true"
              >
                <span class="icon-luc-Sun sac__font-24" />
              </button>
            </div>
          </div>
          <CycleChartIndex 
            :light-mode="lightMode" 
            ref="cycleChartIndex"
            :legend-visibility-state="chartLegendState"
            @legend-visibility-changed="onChartLegendChanged"
          />
        </div>
        <div
          v-else-if="activeTab === 'runtime'"
          class="cd-content-container"
        >
          <DailyTrends />
        </div>
        <div
          v-else-if="activeTab === 'alerts'"
          class="cd-content-container"
        >
          <AlertsTable
            :alertTableData="alertsTabTableData"
            :loading="loadingAlertsTab"
            :changeFilter="filterAlertsTab"
            :zones="hvac"
            :categories="alertTabCategories"
            :alertType="alertShowType"
            :changeAlertsType="toggleAlertType"
          />
        </div>
        <div
          v-else-if="activeTab === 'activity'"
          class="cd-content-container"
        >
          <ActivityTable
            :activityTableData="activityTableData"
            :activityTableSearch="activityTableSearch"
            :loading="activityLoading"
            :changeFilter="filterActivityTab"
            :zones="hvac"
          />
        </div>
        <div
          v-else-if="activeTab === 'orders'"
          class="cd-content-container"
        >
          <CustomerOrderTable />
        </div>
        <div
          v-else-if="activeTab === 'address'"
          class="cd-content-container"
        >
          <ManageAddress />
        </div>
        <div
          v-else-if="activeTab === 'performanceSummary'"
          class="cd-content-container"
        >
          <PerformanceIndex />
        </div>
      </div>
    </div>
    <ActionModal />
  </section>
</template>

<script>
import customerDetailMixin from '@shared/mixins/customerDetailMixin';

import TopInfo from '@shared/components/customerDetail/TopInfo.vue';
import DetailTab from '@shared/components/customerDetail/DetailTab.vue';
import Overview from '@shared/components/customerDetail/Overview.vue';
import CycleFilter from '@shared/components/customerDetail/CycleFilter.vue';
import CycleTable from '@shared/components/customerDetail/CycleTable.vue';
import CycleGraph from '@shared/components/customerDetail/CycleGraph.vue';
import AlertsTable from '@shared/components/customerDetail/AlertsTable.vue';
import DailyTrends from '@shared/components/customerDetail/DailyTrends.vue';
import ActivityTable from '@shared/components/customerDetail/ActivityTable.vue';
import ActionModal from '@shared/components/customerDetail/ActionModal.vue';
import CustomerOrderTable from '@shared/components/customerDetail/CustomerOrderTable.vue';
import PerformanceIndex from '@shared/components/customerDetail/performanceSummary/Index.vue';
import ManageAddress from '@shared/components/customerDetail/ManageAddress.vue';
import CycleChartIndex from '@shared/components/deviceDetail/cycleChart/CycleChartIndex.vue';

export default {
  name: 'CustomerDetail',
  metaInfo: {
    title: 'SmartAC.com | Customer Detail',
  },
  components: {
    CycleChartIndex,
    TopInfo,
    ActionModal,
    PerformanceIndex,
    DailyTrends,
    AlertsTable,
    DetailTab,
    Overview,
    CycleFilter,
    CycleTable,
    CycleGraph,
    ActivityTable,
    CustomerOrderTable,
    ManageAddress,
  },
  mixins: [customerDetailMixin],
  data() {
    return {
      lightMode: true,
      chartLegendState: {},
    };
  },
  watch: {
    selectedZoneSerial(newZoneSerial) {
      this.getRuntimeGraph('zone', newZoneSerial);
      this.filterAlertsTab('zone', newZoneSerial);
    },
    $route(to) {
      const {
        params,
      } = to;
      if (params.customerId && params.customerId !== this.customer.id) {
        this.$store.dispatch('setDetailCustomer', {
          customerId: this.$route.params.customerId,
          activeTab: this.$route.query.activeTab,
        });
      }
    },
  },
  async mounted() {
    this.loadChartLegendState();
    
    await this.$store.dispatch(
      'setDetailCustomer',
      {
        customerId: this.$route.params.customerId,
        activeTab: this.$route.query.activeTab,
      },
    );

    await Promise.all([
      this.$store.dispatch('getPartnerProviderPlans'),
    ]);
  },
  destroyed() {
    this.$store.dispatch('resetCustomerDetail');
  },
  methods: {
    onChartLegendChanged(event) {
      this.chartLegendState = { ...event.visibilityState };
      
      this.saveChartLegendState();
    },
    saveChartLegendState() {
      try {
        localStorage.setItem('chartLegendState', JSON.stringify(this.chartLegendState));
      } catch (error) {
        console.warn('Could not save chart legend state to localStorage:', error);
      }
    },
    loadChartLegendState() {
      try {
        const saved = localStorage.getItem('chartLegendState');
        if (saved) {
          this.chartLegendState = JSON.parse(saved);
        }
      } catch (error) {
        console.warn('Could not load chart legend state from localStorage:', error);
        this.chartLegendState = {};
      }
    },
    resetChartLegend() {
      this.chartLegendState = {};
      
      if (this.$refs.cycleChartIndex && this.$refs.cycleChartIndex.resetLegendVisibility) {
        this.$refs.cycleChartIndex.resetLegendVisibility();
      }
      
      localStorage.removeItem('chartLegendState');
    },
    getChartLegendState() {
      return { ...this.chartLegendState };
    },
    setChartLegendState(state) {
      this.chartLegendState = { ...state };
      
      if (this.$refs.cycleChartIndex && this.$refs.cycleChartIndex.setLegendVisibilityState) {
        this.$refs.cycleChartIndex.setLegendVisibilityState(state);
      }
    }
  },
};
</script>

<style lang="scss">

section#customer-detail {
  @include section-container;

  .loading-container {
    @include loading-container;
  }

  .content-container {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .body-container {
    @include section-body-container;

    .cd-content-container {
      @include section-content-container;
      .toggle-button {
        display: grid;
        justify-content: center;
        align-items: center;
        height: 40px;
        width: 40px;
        border-radius: 50%;
        &.light-active {
          background-color: $SUNSET100;
          color: $BLACK;
        }
        &.dark-active {
          background-color: $NBLUE110;
          color: $WHITE;
        }
      }
    }
  }

  #loom-record-btn {
    position: fixed;
    z-index: 100;
    bottom: 18px;
    left: 18px;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background-color: $NBLUE100;
    color: $WHITE;
    font-weight: 700;
    box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;
  }
}
</style>
